import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { CopyIcon } from "../../components/Icons/copy";
import { LinkIcon } from "../../components/Icons/link";
import { SuccessIcon } from "../../components/Icons/success";
import { palette } from "../../styles/constants";

type GhostLinkProps = {
    href?: string;
    copy?: string;
    target?: React.HTMLAttributeAnchorTarget;
    children?: React.ReactNode;
    showIndicator?: boolean;
};

export const GhostLink = ({
    href,
    copy,
    target = "_blank",
    showIndicator = true,
    children,
}: GhostLinkProps) => {
    const { t } = useTranslation();
    const Container = href ? A : Copy;
    const [focused, setFocused] = useState(false);
    const [copied, setCopied] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const handleCopy = () => {
        try {
            navigator.clipboard.writeText(copy!);
            setCopied(true);
            timeoutRef.current = setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            console.error(e);
        }
    };
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    return (
        <Wrapper
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onMouseEnter={() => setFocused(true)}
            onMouseLeave={() => setFocused(false)}
        >
            <Container
                {...(href ? { href, target } : {})}
                {...(copy
                    ? { onClick: handleCopy, role: "button", tabIndex: 0 }
                    : {})}
                onKeyDown={(e) => {
                    if (copy) {
                        if (e.key === "Enter" || e.key === " ") {
                            handleCopy();
                        }
                    }
                }}
            >
                {children || copy || href?.replace(/^https?:\/\//, "")}

                {showIndicator && (
                    <Indicator>
                        {copy ? (
                            <CopyIcon color={palette.neutral[900]} />
                        ) : (
                            <LinkIcon color={palette.neutral[900]} />
                        )}
                    </Indicator>
                )}
            </Container>
            <Tooltip
                aria-hidden={!focused}
                data-hidden={!focused}
                {...(href ? { href, target, as: "a" } : {})}
                {...(copy
                    ? { onClick: handleCopy, role: "button", tabIndex: -1 }
                    : {})}
            >
                {copied && <SuccessIcon color={palette.white} />}
                {copied
                    ? t("components.ghost_link.copied")
                    : t("components.ghost_link.link", {
                          context: copy ? "copy" : "open",
                      })}
                {copied ? null : copy ? (
                    <CopyIcon color={palette.white} />
                ) : (
                    <LinkIcon color={palette.white} />
                )}
            </Tooltip>
        </Wrapper>
    );
};

const Wrapper = styled.div.attrs({
    className: "ghost-link",
})`
    display: inline-block;
    position: relative;
`;

const Indicator = styled.div.attrs({
    className: "ghost-link-indicator",
})`
    display: inline-flex;
    flex-direction: column;
    align-self: center;
    padding-left: 4px;
`;

const A = styled.a`
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    position: relative;
    &:hover {
        text-decoration: underline;
    }
    &:focus-visible {
        outline: 2px solid #00f;
        outline-offset: 2px;
        border-radius: 1px;
    }
    display: inline-flex;
`;

const Copy = styled.span`
    cursor: pointer;
    color: inherit;
    position: relative;
    &:hover {
        text-decoration: underline;
        & > * {
            text-decoration: underline;
        }
    }
    &:focus-visible {
        outline: 2px solid #00f;
        outline-offset: 2px;
        border-radius: 1px;
    }
    display: inline-flex;
`;

const Tooltip = styled.div`
    &[data-hidden="true"] {
        opacity: 0;
        transform: translateX(-50%) translateY(5px);
        visibility: hidden;
    }
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(0px);
    background-color: #000;
    color: #fff;
    z-index: 10;
    transition: all 0.2s ease;
    padding: 4px 8px;
    white-space: nowrap;
    border-radius: 3px;
    font-size: 12px;
    line-height: 20px;
    font-weight: 500px;
    text-align: center;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    &::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%) translateY(-20%);
        border: 6px solid transparent;
        border-top: 6px solid #000;
    }
`;
